/**
 * Getting QuickTide onto the home screen.
 *
 * A browser tab is not a safe place for this app. iOS throws away a website's
 * storage after seven days without a visit, which on a boat is an ordinary
 * fortnight — the tide tables would be gone exactly when there is no signal to
 * fetch them again. Installed to the home screen the data stays, and the app
 * opens without a browser bar. So on a phone the tab is a dead end we walk the
 * user out of; on a desktop it is merely worth pointing out.
 */
import { computed, ref } from 'vue'

export type GateMode = 'none' | 'iosOther' | 'iosSafari' | 'android' | 'desktop'

const SKIP_KEY = 'quicktide.install'

function ua() {
  return navigator.userAgent
}

/** iPadOS calls itself a Mac; the touch points give it away */
export function isIos(): boolean {
  return /iPad|iPhone|iPod/.test(ua()) || (/Macintosh/.test(ua()) && navigator.maxTouchPoints > 1)
}

/** ?gate=iosSafari&ios=26&ipad=1 shows a screen as another device would get it, to check the copy */
const preview = new URLSearchParams(location.search)

/** An iPad, which puts Safari's buttons in other places than an iPhone does */
export function isIpad(): boolean {
  if (preview.has('ipad')) return preview.get('ipad') !== '0'
  return /iPad/.test(ua()) || (/Macintosh/.test(ua()) && navigator.maxTouchPoints > 1)
}

/**
 * The major version of iOS or iPadOS, because Safari 26 hid the share button behind a ••• menu
 * and the instructions have to name what the reader is actually looking at. The iPad calls
 * itself a Mac and gives no `OS x_y`, but Safari's own version number tracks the system from
 * 26 onwards, which is exactly the boundary that matters here.
 */
export function iosVersion(): number | null {
  const forcedVersion = Number(preview.get('ios'))
  if (Number.isFinite(forcedVersion) && forcedVersion > 0) return forcedVersion
  const os = /OS (\d+)[_\d]* like Mac OS X/.exec(ua())
  if (os) return Number(os[1])
  const safari = /Version\/(\d+)/.exec(ua())
  return safari ? Number(safari[1]) : null
}

export function isAndroid(): boolean {
  return /Android/.test(ua())
}

/**
 * Every browser on iOS runs Safari's engine, but only Safari itself has "Add to
 * Home Screen" — Chrome, Firefox, Edge and the in-app browsers of the social
 * apps cannot install anything.
 */
export function isIosSafari(): boolean {
  return isIos() && !/CriOS|FxiOS|EdgiOS|OPiOS|YaBrowser|DuckDuckGo|Brave|FBAN|FBAV|Instagram|Line|MicroMessenger|Snapchat/i.test(ua())
}

export type DesktopBrowser = 'chrome' | 'edge' | 'firefox' | 'safari' | 'other'

/**
 * Which desktop browser this is, to name the right menu. Order matters: Edge and
 * Opera both call themselves Chrome, and Chrome calls itself Safari.
 */
export function desktopBrowser(): DesktopBrowser {
  const s = ua()
  if (/Edg\//.test(s)) return 'edge'
  if (/OPR\/|Opera/.test(s)) return 'other'
  if (/Firefox\//.test(s)) return 'firefox'
  if (/Chrome\/|Chromium\//.test(s)) return 'chrome'
  if (/Safari\//.test(s)) return 'safari'
  return 'other'
}

export function isInstalled(): boolean {
  const nav = navigator as Navigator & { standalone?: boolean }
  if (nav.standalone === true) return true
  return ['standalone', 'fullscreen', 'minimal-ui', 'window-controls-overlay']
    .some(mode => window.matchMedia(`(display-mode: ${mode})`).matches)
}

/**
 * Chrome, Firefox and Edge for iOS all hand the page to Safari when asked for this
 * scheme — Edge after a confirmation of its own. The in-app browsers of the social
 * apps ignore it, and those users get the written way out instead.
 */
export function safariUrl(): string {
  return `x-safari-${location.href}`
}

interface InstallPromptEvent extends Event {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

let deferred: InstallPromptEvent | null = null
/** Chromium tells us when it is willing to install; Safari and Firefox never do */
const promptable = ref(false)
const skipped = ref(localStorage.getItem(SKIP_KEY) === 'skipped')
const installed = ref(isInstalled())

window.addEventListener('beforeinstallprompt', event => {
  event.preventDefault()
  deferred = event as InstallPromptEvent
  promptable.value = true
})
window.addEventListener('appinstalled', () => {
  installed.value = true
  promptable.value = false
})

const forced = preview.get('gate') as GateMode | null

export function useInstallGate() {
  const mode = computed<GateMode>(() => {
    if (forced) return forced
    if (installed.value) return 'none'
    if (isIos()) return isIosSafari() ? 'iosSafari' : 'iosOther'
    if (skipped.value) return 'none'
    return isAndroid() ? 'android' : 'desktop'
  })
  // the phone gates hold the app shut; the desktop one is a notice beside it
  const blocking = computed(() => mode.value !== 'none' && mode.value !== 'desktop')
  return { mode, blocking, promptable, skip, install }
}

function skip() {
  localStorage.setItem(SKIP_KEY, 'skipped')
  skipped.value = true
}

/** The browser's own install flow, when it offers one */
export async function install(): Promise<void> {
  if (!deferred) return
  await deferred.prompt()
  const choice = await deferred.userChoice
  deferred = null
  promptable.value = false
  if (choice.outcome === 'accepted') installed.value = true
}
